import React, { Fragment, memo, useContext, useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import { FiCheck, FiEdit } from "react-icons/fi";
import { Close } from "@mui/icons-material";

import styles from "./styles";
import flex from "../../../styles/flex";
import { EditableContainerContext } from "../EditableContainer/EditableContainer";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";

interface IEditableGroupState {
    isEditing: boolean,
    data: unknown,
    setData: (data: unknown, rebuild?: boolean) => void;
    buildId: number;
}

export const EditableGroupContext = React.createContext<Partial<IEditableGroupState>>({});


export enum EditableGroupType {
    none = 0,
    alwaysEditing = 1,
    switchableEditing = 2,
}


export type EditableGroup<T> = {
    id: string,
    onDataChanged: (data: T) => Promise<boolean>;
    data: T;
    dataFields?: string[];
    editType?: EditableGroupType;
    children: React.ReactNode,
    forceEdit?: boolean;
    delay?: number;
    onCancelEdit?: () => void;
    onValidate?: (data: T) => Promise<boolean>;
    testId: string;
};

const propertiesHaveChanged = (
    data1: Record<string, unknown>,
    data2: Record<string, unknown>,
    properties: string[] | undefined,
) => {
    if (!properties) {
        // If no properties specified, maintain previous behaviour
        // Comparing the whole object breaks the time picker on booking page
        return true;
    }

    const filterObject = (obj: Record<string, unknown>, properties: string[]) => {
        return properties
            .reduce((acc, key) => {
                return {
                    ...acc,
                    [key]: obj[key],
                }
            }, {} as Record<string, unknown>);
    }

    const a = filterObject(data1, properties);
    const b = filterObject(data2, properties);
    return JSON.stringify(a) !== JSON.stringify(b);
}

function component<T>(props: React.PropsWithChildren<EditableGroup<T>>) {
    const [buildId, setBuildId] = useState<number>(0);
    const [dataCache, setDataCache] = useState<T>(props.data);
    const [editing, setEditing] = useState(false);
    let editType = props.editType == null ? EditableGroupType.none : props.editType;
    const isEditing = editing || editType == EditableGroupType.alwaysEditing;

    const isPreventSavingBlankBookingFieldsReleased = useReleaseManagement(
        ReleaseFlag.releasePreventSavingBlankBookingFields
    );

    const [savingChanges, setSavingChanges] = useState(false);
    const container = useContext(EditableContainerContext);

    if (editType == EditableGroupType.switchableEditing) {
        if (container != null) {
            if (container.group != null && container.group != props.id) {
                editType = EditableGroupType.none;
            }
        }
    }

    useEffect(() => {
        if (JSON.stringify(dataCache) === JSON.stringify(props.data)) {
            return;
        }

        setDataCache(props.data);
    }, [props.data]);

    const [timeoutCancel, setTimeoutCancel] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (editType == EditableGroupType.alwaysEditing) {
            if (!propertiesHaveChanged(
                dataCache as Record<string, unknown>,
                props.data as Record<string, unknown>,
                props.dataFields)
            ) {
                return;
            }

            if (timeoutCancel != null) {
                clearTimeout(timeoutCancel);
            }

            setTimeoutCancel(setTimeout(() => {
                props.onDataChanged(dataCache as T);
            }, props.delay == null ? 1 : props.delay));
        }
    }, [dataCache]);

    useEffect(() => {
        if (props.forceEdit == true) {
            setEditing(true);
        } else if (props.forceEdit == false) {
            setEditing(false);
        }

    }, [props.forceEdit, props.data]);

    return (
        <EditableGroupContext.Provider value={{
            isEditing,
            data: dataCache,
            setData: (data, rebuild) => {
                setDataCache(data as T);
                if (rebuild == true) {
                    setBuildId(buildId + 1);
                }

            },
            buildId: buildId
        }}>
            <form autoComplete={"off"} noValidate data-testid={`${props.testId}-container`}>
                {
                    (savingChanges != true && isEditing == true && editType == EditableGroupType.switchableEditing) && (<Fragment>
                        <div style={styles.leftButton}>
                            <IconButton
                                data-testid={`${props.testId}-cancel-edit-button`}
                                onClick={() => {
                                    if (props.onCancelEdit) {
                                        props.onCancelEdit();
                                    }

                                    setEditing(false);
                                    setDataCache(props.data);

                                    if (container != null) {
                                        if (container.setGroup != null) {
                                            container.setGroup(null);
                                        }
                                    }
                                }}
                            >
                                <Close style={styles.cancelButtonIcon} />
                            </IconButton>
                        </div>
                        <div style={styles.rightButton}>
                            <IconButton
                                data-testid={`${props.testId}-save-button`}
                                onClick={async () => {
                                    setSavingChanges(true);

                                    let canProceed = true;

                                    if (isPreventSavingBlankBookingFieldsReleased && props.onValidate) {
                                        canProceed = await props.onValidate(dataCache as T);
                                    }

                                    if (canProceed && (await props.onDataChanged(dataCache as T)) != false) {
                                        setEditing(false);
                                        if (container != null) {
                                            if (container.setGroup != null) {
                                                container.setGroup(null);
                                            }
                                        }
                                    }
                                    setSavingChanges(false);
                                }}
                            >
                                <FiCheck style={styles.commitButtonIcon} />
                            </IconButton>
                        </div>
                    </Fragment>)
                }
                {
                    (savingChanges != true && isEditing != true && editType == EditableGroupType.switchableEditing) && (
                        <Fragment>
                            <div style={styles.rightButton}>
                                <IconButton
                                    data-testid={`${props.testId}-edit-button`}
                                    onClick={() => {
                                        setEditing(true)

                                        if (container != null) {
                                            if (container.setGroup != null) {
                                                container.setGroup(props.id);
                                            }
                                        }
                                    }}
                                >
                                    <FiEdit style={styles.editButtonIcon} />
                                </IconButton>
                            </div>
                        </Fragment>
                    )
                }
                <Fragment>
                    {props.children}
                    {isEditing == true && (
                        <div style={{ ...flex.flexFill, marginTop: 20 }}></div>
                    )}
                </Fragment>

            </form>
        </EditableGroupContext.Provider>
    );
}

export const EditableGroup = memo(component) as typeof component;


/*
EditableGroup.propTypes = {
    id: propTypes.string.isRequired,
    children: propTypes.func.isRequired
}
*/
export default EditableGroup;
